/* eslint-disable @next/next/no-img-element */
import { Fragment } from "react";
import { Formik } from "formik";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import saveEdit from "utils/saveEdit";

export default function EditResortPanel({ open, setOpen, resort }) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-30 overflow-hidden"
        onClose={setOpen}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0" />

          <div className="fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-2xl">
                <Formik
                  initialValues={{
                    resortName: resort.Name,
                    mainImage: resort.MainPhoto,
                    mainImageCaption: resort.MainPhoto?.caption || "",
                  }}
                  onSubmit={(values, { setSubmitting }) =>
                    saveEdit(values, setSubmitting, resort, setOpen)
                  }
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    isSubmitting,
                    /* and other goodies */
                  }) => (
                    <form
                      className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl"
                      onSubmit={handleSubmit}
                    >
                      <div className="flex-1">
                        {/* Header */}
                        <div className="bg-gray-50 px-4 py-6 sm:px-6">
                          <div className="flex items-start justify-between space-x-3">
                            <div className="space-y-1">
                              <Dialog.Title className="text-lg font-medium text-gray-900">
                                Edit Resort
                              </Dialog.Title>
                            </div>
                            <div className="flex h-7 items-center">
                              <button
                                type="button"
                                className="text-gray-400 hover:text-gray-500"
                                onClick={() => setOpen(false)}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Divider container */}
                        <div className="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0">
                          {/* Resort name */}
                          <div className="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                            <div>
                              <label
                                htmlFor="resortName"
                                className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                              >
                                Resort name
                              </label>
                            </div>
                            <div className="sm:col-span-2">
                              <input
                                type="text"
                                name="resortName"
                                id="resortName"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.resortName}
                              />
                            </div>
                          </div>

                          {/* Main image */}
                          <div className="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                            <div>
                              <label
                                htmlFor="mainImage"
                                className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                              >
                                Main Image
                              </label>
                            </div>
                            <div className="sm:col-span-2">
                              {values?.mainImage?.hash && (
                                <div className="aspect-video relative mb-4 w-full">
                                  <img
                                    src={`https://images.opencoaster.com/800x400/${values.mainImage.hash}${values.mainImage.ext}`}
                                    alt=""
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              {values?.mainImage instanceof File && (
                                <div className="aspect-video relative mb-4 w-full">
                                  <img
                                    src={URL.createObjectURL(values.mainImage)}
                                    alt=""
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              <input
                                type="file"
                                id="mainImage"
                                name="mainImage"
                                accept="image/jpg,image/jpeg"
                                onChange={(event) => {
                                  setFieldValue(
                                    "mainImage",
                                    event.currentTarget.files[0]
                                  );
                                }}
                                className="mb-4"
                              />
                              <div className="text-sm">
                                <label
                                  htmlFor="mainImageCaption"
                                  className="font-medium text-gray-700"
                                >
                                  Caption
                                </label>
                                <input
                                  type="text"
                                  name="mainImageCaption"
                                  id="mainImageCaption"
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.mainImageCaption}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex-shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                        <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                            className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            onClick={() => setOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            disabled={isSubmitting}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
